import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReportsWorkspaceComponent } from './reports-workspace.component';
import { ReportContextOption } from '../../models/report-context-option';
import { TournamentService } from '../../services/tournament/tournament.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { ReportContextOptionsService } from '../../services/reports/report-context-options.service';

@Component({
    selector: 'app-tournament-reports',
    standalone: true,
    imports: [ReportsWorkspaceComponent],
    template: `
        <app-reports-workspace
            [helpText]="helpText"
            [initialContext]="context()"
            [roundOptions]="roundOptions()"
            [courseOptions]="courseOptions()">
        </app-reports-workspace>
    `
})
export class TournamentReportsComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly tournamentService = inject(TournamentService);
    private readonly loggingService = inject(LoggingService);
    private readonly contextOptionsService = inject(ReportContextOptionsService);

    private activeTournamentKey: string | null = null;

    readonly helpText = 'Download tournament-level reports. Adjust the context fields if you need to target a different tournament or round.';

    readonly roundOptions = signal<ReportContextOption[]>([]);
    readonly courseOptions = signal<ReportContextOption[]>([]);

    readonly context = computed(() => {
        const routeTournamentId = this.route.snapshot.paramMap.get('tournamentId');
        const selectedTournament = this.tournamentService.selectedTournamentSignal();
        const selectedRound = this.tournamentService.selectedRoundSignal();

        const tournamentId = routeTournamentId || (selectedTournament ? selectedTournament.tournamentId || null : null);
        const roundId = selectedRound ? selectedRound.roundId || null : null;

        return {
            tournamentId,
            roundId,
            courseId: null
        };
    });

    constructor() {
        effect(() => {
            const tournament = this.tournamentService.selectedTournamentSignal();
            if (!tournament?.tournamentId) {
                this.activeTournamentKey = null;
                this.roundOptions.set([]);
                this.courseOptions.set([]);
                return;
            }

            const tournamentKey = String(tournament.tournamentId);
            this.activeTournamentKey = tournamentKey;

            this.contextOptionsService.getRoundOptions(tournamentKey)
                .then(options => {
                    if (this.activeTournamentKey === tournamentKey) {
                        this.roundOptions.set(options);
                    }
                })
                .catch(error => this.loggingService.error('TournamentReportsComponent', 'Failed to load rounds for reports workspace', error));

            this.contextOptionsService.getCourseOptions(tournamentKey)
                .then(options => {
                    if (this.activeTournamentKey === tournamentKey) {
                        this.courseOptions.set(options);
                    }
                })
                .catch(error => this.loggingService.error('TournamentReportsComponent', 'Failed to load courses for reports workspace', error));
        });
    }
}
